import React, {
    useEffect,
    useRef,
    useState
} from "react";
  import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    Card,
    CardContent,
    Typography,
    Box,
    TextField,
    Button,
    CircularProgress,
    Alert
} from "@mui/material";

import {
    getFreightAndProformaCurrentMonth,
    getFreightAndProformaOnCost,
    saveFreightAndProformaOnCost
} from "../../../services/freightAndProformaOnCostService";
import PageHeader from "../../../utils/PageHeader";


// ============================================================
// MONTH NAMES
// ============================================================

const monthNames = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];


// ============================================================
// COMPONENT
// ============================================================

const FreightAndProformaOnCostDataEntry = () => {


    // ========================================================
    // STATE
    // ========================================================

    const [month, setMonth] =
        useState("");

    const [year, setYear] =
        useState("");


    const [storeFreight, setStoreFreight] =
        useState("0");


    const [proformaOnCost, setProformaOnCost] =
        useState("0");


    const [loading, setLoading] =
        useState(true);


    const [saving, setSaving] =
        useState(false);


    const [message, setMessage] =
        useState("");


    const [messageType, setMessageType] =
        useState("");


    // ========================================================
    // REFS
    // ========================================================

    const storeFreightRef =
        useRef(null);


    const proformaRef =
        useRef(null);


    const saveRef =
        useRef(null);


    // ========================================================
    // MONTH NAME
    // ========================================================

    const monthName =
        month
            ? monthNames[Number(month)]
            : "";


    // ========================================================
    // LOAD CURRENT MONTH
    // ========================================================

    const loadCurrentMonth = async () => {

        try {

            setLoading(true);

            setMessage("");

            setMessageType("");


            const res =
                await getFreightAndProformaCurrentMonth();


            const data =
                res.data.data;


            const currentMonth =
                Number(data.month);


            const currentYear =
                Number(data.year);


            setMonth(currentMonth);

            setYear(currentYear);


            // IMPORTANT:
            // Use returned values directly.

            await loadFreightAndProforma(
                currentMonth,
                currentYear
            );


        } catch (err) {

            console.error(
                "Current month error:",
                err
            );


            setMessage(
                err.response?.data?.message ||
                "Unable to load current month"
            );


            setMessageType("error");


        } finally {

            setLoading(false);

        }

    };


    // ========================================================
    // LOAD STORE FREIGHT / PROFORMA
    // ========================================================

    const loadFreightAndProforma = async (
        selectedMonth,
        selectedYear
    ) => {

        try {

            const res =
                await getFreightAndProformaOnCost(
                    selectedMonth,
                    selectedYear
                );


            const data =
                res.data.data;


            if (data) {

                // --------------------------------------------
                // STORE FREIGHT
                // --------------------------------------------

                setStoreFreight(

                    data.StoreFreight !== null &&
                    data.StoreFreight !== undefined

                        ? String(data.StoreFreight)

                        : "0"

                );


                // --------------------------------------------
                // PROFORMA ON COST
                // --------------------------------------------

                setProformaOnCost(

                    data.proformaoncost !== null &&
                    data.proformaoncost !== undefined

                        ? String(data.proformaoncost)

                        : "0"

                );

            } else {

                // --------------------------------------------
                // NO ENTRY
                // --------------------------------------------

                setStoreFreight("0");

                setProformaOnCost("0");

            }


        } catch (err) {

            console.error(
                "Freight Proforma load error:",
                err
            );


            setMessage(
                err.response?.data?.message ||
                "Unable to load Store Freight and Proforma data"
            );


            setMessageType("error");

        }

    };


    // ========================================================
    // INITIAL LOAD
    // ========================================================

    useEffect(() => {

        loadCurrentMonth();

    }, []);


    // ========================================================
    // SAVE
    // ========================================================

    const handleSave = async () => {

        setMessage("");

        setMessageType("");


        // ----------------------------------------------------
        // GET TEXT VALUES
        // ----------------------------------------------------

        const storeFreightText =
            String(storeFreight).trim();


        const proformaText =
            String(proformaOnCost).trim();


        // ----------------------------------------------------
        // VALIDATE STORE FREIGHT
        // ----------------------------------------------------

        if (

            storeFreightText === "" ||

            !/^-?\d+(\.\d+)?$/.test(
                storeFreightText
            )

        ) {

            setMessage(
                "Please enter a valid Store Freight amount"
            );


            setMessageType("error");


            storeFreightRef.current?.focus();


            return;

        }


        // ----------------------------------------------------
        // VALIDATE PROFORMA
        // ----------------------------------------------------

        if (

            proformaText === "" ||

            !/^-?\d+(\.\d+)?$/.test(
                proformaText
            )

        ) {

            setMessage(
                "Please enter a valid Proforma on Cost amount"
            );


            setMessageType("error");


            proformaRef.current?.focus();


            return;

        }


        // ----------------------------------------------------
        // CONVERT
        // ----------------------------------------------------

        const storeFreightValue =
            Number(storeFreightText);


        const proformaValue =
            Number(proformaText);


        // ----------------------------------------------------
        // EXTRA SAFETY
        // ----------------------------------------------------

        if (
            !Number.isFinite(
                storeFreightValue
            )
        ) {

            setMessage(
                "Please enter a valid Store Freight amount"
            );


            setMessageType("error");


            storeFreightRef.current?.focus();


            return;

        }


        if (
            !Number.isFinite(
                proformaValue
            )
        ) {

            setMessage(
                "Please enter a valid Proforma on Cost amount"
            );


            setMessageType("error");


            proformaRef.current?.focus();


            return;

        }


        // ----------------------------------------------------
        // MONTH / YEAR
        // ----------------------------------------------------

        if (!month || !year) {

            setMessage(
                "Month and year not available"
            );


            setMessageType("error");


            return;

        }


        // ====================================================
        // SAVE
        // ====================================================

        try {

            setSaving(true);


            const payload = {

                StoreFreight:
                    storeFreightValue,


                proformaoncost:
                    proformaValue,


                PMONTH:
                    Number(month),


                PYEAR:
                    Number(year),

                month:month,
                year:year

            };


            const res =
                await saveFreightAndProformaOnCost(
                    payload
                );


            setMessage(
                res.data.message ||
                "Store Freight and Proforma data saved successfully"
            );


            setMessageType("success");


            // ------------------------------------------------
            // RELOAD
            // ------------------------------------------------

            await loadFreightAndProforma(
                Number(month),
                Number(year)
            );


            // ------------------------------------------------
            // RETURN TO FIRST FIELD
            // ------------------------------------------------

            setTimeout(() => {

                storeFreightRef.current?.focus();

                storeFreightRef.current?.select();

            }, 100);


        } catch (err) {

            console.error(
                "Freight Proforma save error:",
                err
            );


            setMessage(
                err.response?.data?.message ||
                "Unable to save Store Freight and Proforma data"
            );


            setMessageType("error");


        } finally {

            setSaving(false);
 await loadFreightAndProforma(
                Number(month),
                Number(year)
            );
        }

    };


    // ========================================================
    // ENTER - STORE FREIGHT
    // ========================================================

    const handleStoreFreightKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();


            proformaRef.current?.focus();

            proformaRef.current?.select();

        }

    };


    // ========================================================
    // ENTER - PROFORMA
    // ========================================================

    const handleProformaKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();


            saveRef.current?.focus();

        }

    };


    // ========================================================
    // ENTER - SAVE
    // ========================================================

    const handleSaveKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();


            if (!saving) {

                handleSave();

            }

        }

    };


    // ========================================================
    // LOADING
    // ========================================================

    if (loading) {

        return (

            <Box
                sx={{
                    width: "100%",

                    minHeight: "200px",

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center"
                }}
            >

                <CircularProgress />

            </Box>

        );

    }


    // ========================================================
    // MAIN UI
    // ========================================================

    return (

        <Box
            sx={{
                width: "100%",

                boxSizing: "border-box",

                p: {
                    xs: 0.5,
                    sm: 1,
                    md: 1.5
                }
            }}
        >


            {/* =================================================
                HEADER
            ================================================= */}

            <PageHeader
        title=" Store Proforma on Cost and Freight Data Entry"
        subtitle={`Data entry status for the month of: ${monthName} - ${year}`}
        icon={AccessTimeIcon}
       // statusLabel={'Records: ' + totalRows}
       //  statusValue="CSV File"
        // statusChip={{
        //   label: loading
        //     ? "Loading..."
        //     : totalRows > 0
        //       ? "Data Found"
        //       : "No Data",
        //   color: loading ? "warning" : totalRows > 0 ? "primary" : "default",
        // }}
        //showRefresh
       // onRefresh={handleRefresh}
        // refreshing={loading}
      />




    


            {/* =================================================
                ENTRY CARD
            ================================================= */}

            <Card
                elevation={2}

                sx={{

                    width: "100%",

                    borderRadius: 2,

                    boxSizing: "border-box"

                }}
            >

                <CardContent
                    sx={{

                        p: {

                            xs: 1.5,

                            sm: 2,

                            md: 2.5

                        },

                        "&:last-child": {

                            pb: {

                                xs: 1.5,

                                sm: 2,

                                md: 2.5

                            }

                        }

                    }}
                >


                    {/* =========================================
                        ENTRY AREA
                    ========================================= */}

                    <Box
                        sx={{

                            width: "100%",

                            display: "flex",

                            flexDirection: {

                                xs: "column",

                                sm: "row"

                            },

                            alignItems: {

                                xs: "stretch",

                                sm: "center"

                            },

                            gap: {

                                xs: 1.5,

                                sm: 2,

                                md: 3

                            },

                            boxSizing: "border-box"

                        }}
                    >


                        {/* =====================================
                            STORE FREIGHT
                        ===================================== */}

                        <Box
                            sx={{

                                display: "flex",

                                alignItems: "center",

                                gap: {

                                    xs: 1,

                                    sm: 1.5

                                },

                                flex: 1,

                                minWidth: 0

                            }}
                        >

                            <Typography
                                component="label"

                                htmlFor="storeFreight"

                                sx={{

                                    color: "#e60023",

                                    fontWeight: 700,

                                    fontSize: {

                                        xs: "0.9rem",

                                        sm: "0.95rem"

                                    },

                                    whiteSpace: "nowrap"

                                }}
                            >

                                Store Freight (5%)

                            </Typography>


                            <TextField
                                id="storeFreight"

                                inputRef={
                                    storeFreightRef
                                }

                                type="text"

                                inputMode="decimal"

                                value={
                                    storeFreight
                                }

                                onChange={(e) => {

                                    setStoreFreight(
                                        e.target.value
                                    );

                                }}

                                onKeyDown={
                                    handleStoreFreightKeyDown
                                }

                                autoComplete="off"

                                size="small"

                                fullWidth

                                sx={{

                                    maxWidth: {

                                        xs: "100%",

                                        sm: 100

                                    },

                                    "& input": {

                                        fontSize: {

                                            xs: "16px",

                                            sm: "15px"

                                        }

                                    }

                                }}

                            />

                        </Box>


                        {/* =====================================
                            PROFORMA ON COST
                        ===================================== */}

                        <Box
                            sx={{

                                display: "flex",

                                alignItems: "center",

                                gap: {

                                    xs: 1,

                                    sm: 1.5

                                },

                                flex: 1,

                                minWidth: 0

                            }}
                        >

                            <Typography
                                component="label"

                                htmlFor="proformaOnCost"

                                sx={{

                                    color: "#e60023",

                                    fontWeight: 700,

                                    fontSize: {

                                        xs: "0.9rem",

                                        sm: "0.95rem"

                                    },

                                    whiteSpace: "nowrap"

                                }}
                            >

                                Proforma on Cost(%)

                            </Typography>


                            <TextField
                                id="proformaOnCost"

                                inputRef={
                                    proformaRef
                                }

                                type="text"

                                inputMode="decimal"

                                value={
                                    proformaOnCost
                                }

                                onChange={(e) => {

                                    setProformaOnCost(
                                        e.target.value
                                    );

                                }}

                                onKeyDown={
                                    handleProformaKeyDown
                                }

                                autoComplete="off"

                                size="small"

                                fullWidth

                                sx={{

                                    maxWidth: {

                                        xs: "100%",

                                        sm: 100

                                    },

                                    "& input": {

                                        fontSize: {

                                            xs: "16px",

                                            sm: "15px"

                                        }

                                    }

                                }}

                            />

                        </Box>


                        {/* =====================================
                            SAVE
                        ===================================== */}

                        <Button
                            ref={saveRef}

                            variant="contained"

                            type="button"

                            onClick={handleSave}

                            onKeyDown={
                                handleSaveKeyDown
                            }

                            disabled={saving}

                            sx={{

                                minWidth: {

                                    xs: "100%",

                                    sm: 90

                                },

                                height: 40,

                                flexShrink: 0,

                                bgcolor: "#ffff00",

                                color: "#000000",

                                border:
                                    "1px solid #d6b500",

                                fontWeight: 700,

                                "&:hover": {

                                    bgcolor: "#ffeb00"

                                }

                            }}
                        >

                            {saving ? (

                                <>

                                    <CircularProgress
                                        size={20}

                                        sx={{
                                            mr: 1,
                                            color: "#000"
                                        }}
                                    />

                                    Saving...

                                </>

                            ) : (

                                "Save"

                            )}

                        </Button>

                    </Box>


                    {/* =================================================
                        MESSAGE
                    ================================================= */}

                    {message && (

                        <Alert

                            severity={

                                messageType === "success"

                                    ? "success"

                                    : "error"

                            }

                            onClose={() => {

                                setMessage("");

                                setMessageType("");

                            }}

                            sx={{
                                mt: 2
                            }}

                        >

                            {message}

                        </Alert>

                    )}

                </CardContent>

            </Card>

        </Box>

    );

};


export default FreightAndProformaOnCostDataEntry;